import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import GoogleShipmentMap from './GoogleShipmentMap';
import { addOrdersToShipment, createManualShipment, getManualShipmentCandidates, getShipmentAddOrderCandidates, getShipmentRoutePreview } from '../config/api';
import './ManualShipmentModal.css';

const VEHICLES = [
  { value: 'fourWheeler', label: 'Four-wheeler', note: 'Best for panels and multi-product loads' },
  { value: 'twoWheeler', label: 'Two-wheeler', note: 'One inverter, battery, or BOS unit; no panels' },
];

const emptyData = { date: '', hubs: [], selectedHub: null, orders: [], drivers: [], unavailableOrders: [] };
const normalize = value => String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');

const groupStops = orders => {
  const stops = new Map();
  orders.forEach(order => {
    const key = normalize(order.address) || `${order.area}|${order.pincode}`;
    const current = stops.get(key);
    if (current) current.orderIds.push(order.id);
    else stops.set(key, { address: order.address, pincode: order.pincode, area: order.area, orderIds: [order.id], location: order.location, locationSource: order.locationSource });
  });
  return [...stops.values()];
};

const loadTotals = orders => orders.reduce((totals, order) => ({
  points: totals.points + Number(order.loadPoints || 0),
  panels: totals.panels + Number(order.load?.panels || 0),
  inverters: totals.inverters + Number(order.load?.inverters || 0),
  batteries: totals.batteries + Number(order.load?.batteries || 0),
  bos: totals.bos + Number(order.load?.bos || 0),
}), { points: 0, panels: 0, inverters: 0, batteries: 0, bos: 0 });

const mergeStops = (existingStops, addedStops) => {
  const merged = new Map();
  [...(existingStops || []), ...addedStops].forEach(stop => {
    const key = normalize(stop.address) || `${stop.area}|${stop.pincode}`;
    const orderIds = stop.orderIds || [];
    const current = merged.get(key);
    if (current) current.orderIds = [...new Set([...current.orderIds, ...orderIds])];
    else merged.set(key, { ...stop, orderIds: [...orderIds] });
  });
  return [...merged.values()];
};

const ManualShipmentModal = ({ initialHubId, shipment = null, onClose, onCreated, onOrdersAdded }) => {
  const isAdding = Boolean(shipment?.id);
  const [data, setData] = useState(emptyData);
  const [hubId, setHubId] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [vehicleType, setVehicleType] = useState('fourWheeler');
  const [driverId, setDriverId] = useState('');
  const [search, setSearch] = useState('');
  const [selectedOnly, setSelectedOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [routeError, setRouteError] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const routeRequestId = useRef(0);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previousOverflow; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const request = isAdding
      ? getShipmentAddOrderCandidates(shipment.id)
      : getManualShipmentCandidates();
    request.then(response => {
      if (cancelled) return;
      const result = response.data || emptyData;
      setData(result);
      if (isAdding) {
        setHubId(String(result.selectedHub?.warehouseId || ''));
        setVehicleType(result.shipment?.vehicleType || shipment.vehicleType || 'fourWheeler');
        setLoading(false);
        return;
      }
      const enabled = (result.hubs || []).filter(hub => hub.enabled);
      const preferred = enabled.find(hub => String(hub.warehouseId) === String(initialHubId)) || enabled[0];
      setHubId(preferred ? String(preferred.warehouseId) : '');
      if (!preferred) setLoading(false);
    }).catch(requestError => {
      if (!cancelled) { setError(requestError.message || 'Could not load shipment hubs'); setLoading(false); }
    });
    return () => { cancelled = true; };
  }, [initialHubId, isAdding, shipment?.id, shipment?.vehicleType]);

  useEffect(() => {
    if (!hubId || isAdding) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    setSelectedIds([]);
    setDriverId('');
    setPreview(null);
    getManualShipmentCandidates(hubId).then(response => {
      if (!cancelled) setData(response.data || emptyData);
    }).catch(requestError => {
      if (!cancelled) setError(requestError.message || 'Could not load orders for this hub');
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [hubId, isAdding]);

  const selectedOrders = useMemo(() => {
    const selected = new Set(selectedIds);
    return data.orders.filter(order => selected.has(order.id));
  }, [data.orders, selectedIds]);
  const addedStops = useMemo(() => groupStops(selectedOrders), [selectedOrders]);
  const stops = useMemo(() => isAdding ? mergeStops(data.shipment?.stops || shipment?.stops, addedStops) : addedStops,
    [addedStops, data.shipment?.stops, isAdding, shipment?.stops]);
  const selectedTotals = useMemo(() => loadTotals(selectedOrders), [selectedOrders]);
  const totals = useMemo(() => isAdding ? {
    points: Number(data.shipment?.loadPoints ?? shipment?.loadPoints ?? 0) + selectedTotals.points,
    panels: Number(data.shipment?.panels ?? shipment?.panels ?? 0) + selectedTotals.panels,
    inverters: Number(data.shipment?.inverters ?? shipment?.inverters ?? 0) + selectedTotals.inverters,
    batteries: Number(data.shipment?.batteries ?? shipment?.batteries ?? 0) + selectedTotals.batteries,
    bos: Number(data.shipment?.bos ?? shipment?.bos ?? 0) + selectedTotals.bos,
  } : selectedTotals, [data.shipment, isAdding, selectedTotals, shipment]);
  const finalOrderCount = (isAdding ? Number(data.shipment?.orderCount ?? shipment?.orderCount ?? 0) : 0) + selectedOrders.length;
  const capacity = vehicleType === 'twoWheeler'
    ? Number(data.selectedHub?.twoWheelerCapacityPoints || 0)
    : Number(data.selectedHub?.capacityPoints || 0);
  const utilization = capacity ? Math.round((totals.points / capacity) * 100) : 0;
  const twoWheelerUnits = totals.inverters + totals.batteries + totals.bos;
  const twoWheelerInvalid = vehicleType === 'twoWheeler'
    && (totals.panels > 0 || twoWheelerUnits !== 1 || finalOrderCount !== 1);
  const stopLimitExceeded = stops.length > Number(data.selectedHub?.maxStops || 25);
  const maxOrders = Number(data.selectedHub?.maxOrders || 0);
  const orderLimitExceeded = maxOrders > 0 && finalOrderCount > maxOrders;

  const visibleOrders = useMemo(() => {
    const query = normalize(search);
    const selected = new Set(selectedIds);
    return data.orders.filter(order => {
      if (selectedOnly && !selected.has(order.id)) return false;
      if (!query) return true;
      return [order.id, order.customer, order.address, order.area, order.pincode].some(value => normalize(value).includes(query));
    });
  }, [data.orders, search, selectedIds, selectedOnly]);

  const toggleOrder = orderId => {
    setSelectedIds(current => current.includes(orderId) ? current.filter(id => id !== orderId) : [...current, orderId]);
  };

  const previewShipment = useMemo(() => ({
    id: isAdding ? shipment.id : 'Route preview',
    hub: data.selectedHub?.hub || 'Hub',
    hubLocation: data.selectedHub?.hubLocation,
    encodedPolyline: preview?.encodedPolyline || '',
    routeStatus: preview?.routeUnavailable ? 'failed' : preview ? 'ready' : 'pending',
    stops: stops.map((stop, index) => ({ ...stop, sequence: index + 1, orders: stop.orderIds.length, deliveredOrderIds: [], completed: false })),
    deliveryStops: [],
  }), [data.selectedHub, isAdding, preview, shipment?.id, stops]);

  useEffect(() => {
    const requestId = routeRequestId.current + 1;
    routeRequestId.current = requestId;
    setRouteError('');

    if (!stops.length || !data.selectedHub?.hubAddress || (isAdding && !selectedIds.length)) {
      setPreview(null);
      setPreviewing(false);
      return undefined;
    }

    setPreviewing(true);
    const timer = window.setTimeout(async () => {
      try {
        const response = await getShipmentRoutePreview({
          origin: data.selectedHub.hubAddress,
          stops: stops.map(stop => stop.location || stop.address),
        });
        if (routeRequestId.current !== requestId) return;
        setPreview(response.data || null);
      } catch (requestError) {
        if (routeRequestId.current !== requestId) return;
        setPreview({ encodedPolyline: '', routeUnavailable: true });
        setRouteError(requestError.message || 'Road route is unavailable; showing address markers instead.');
      } finally {
        if (routeRequestId.current === requestId) setPreviewing(false);
      }
    }, 450);

    return () => window.clearTimeout(timer);
  }, [data.selectedHub?.hubAddress, isAdding, selectedIds.length, stops]);

  const handleCreate = async () => {
    if (!selectedOrders.length || saving || twoWheelerInvalid || stopLimitExceeded || orderLimitExceeded) return;
    setSaving(true);
    setError('');
    try {
      if (isAdding) {
        const response = await addOrdersToShipment(shipment.id, selectedIds);
        const updated = response.data;
        if (!updated?.id) throw new Error('Orders were added but the refreshed shipment was not returned');
        if (updated.routeStatus === 'failed') toast.warning('Orders added. The road route is unavailable, so address and pincode markers are being used.');
        else toast.success(`${selectedIds.length} order${selectedIds.length === 1 ? '' : 's'} added to ${updated.id}.`);
        onOrdersAdded?.(updated);
      } else {
        const response = await createManualShipment({
          warehouseId: Number(hubId), orderIds: selectedIds, vehicleType, driverId: driverId || null,
        });
        const result = response.data || {};
        const created = result.shipment;
        if (!created?.id) throw new Error('Shipment was created but its ID was not returned');
        if (result.warnings?.length) toast.warning('Shipment created. Google route is unavailable, so address and pincode markers are being used.');
        else toast.success(`${created.id} created with ${created.orderCount} order${created.orderCount === 1 ? '' : 's'}.`);
        onCreated(created);
      }
    } catch (requestError) {
      const message = requestError.message || (isAdding ? 'Could not add the selected orders' : 'Could not create the shipment');
      setError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const canCreate = selectedOrders.length > 0 && !loading && !saving && !twoWheelerInvalid && !stopLimitExceeded && !orderLimitExceeded;

  return <div className="manual-shipment-backdrop" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget && !saving) onClose(); }}>
    <section className="manual-shipment-modal" role="dialog" aria-modal="true" aria-labelledby="manual-shipment-title">
      <header className="manual-shipment-header">
        <div><span>{isAdding ? 'SHIPMENT EDITOR' : 'MANUAL PLANNING'}</span><h2 id="manual-shipment-title">{isAdding ? `Add orders to ${shipment.id}` : 'Create shipment'}</h2><p>{isAdding ? 'Add complete, unallocated orders due today or overdue. The route refreshes automatically.' : 'Select complete orders due today or overdue. Routes start and return to the selected hub.'}</p></div>
        <button type="button" onClick={onClose} disabled={saving} aria-label="Close shipment dialog">×</button>
      </header>

      <div className="manual-shipment-body">
        <div className="manual-shipment-orders-pane">
          <div className="manual-shipment-controls">
            <label><span>Hub</span><select value={hubId} onChange={event => setHubId(event.target.value)} disabled={saving || isAdding}><option value="">Choose hub</option>{data.hubs.map(hub => <option key={hub.warehouseId} value={hub.warehouseId} disabled={!hub.enabled && !isAdding}>{hub.hub}{hub.enabled || isAdding ? '' : ' (disabled)'}</option>)}</select></label>
            <label className="manual-shipment-search"><span>Find an order</span><input type="search" value={search} onChange={event => setSearch(event.target.value)} placeholder="Order ID, customer, address or pincode" /></label>
          </div>
          <div className="manual-shipment-order-toolbar">
            <div><strong>{selectedOrders.length}</strong> selected <span>·</span> {data.orders.length} due or overdue</div>
            <button type="button" className={selectedOnly ? 'is-active' : ''} onClick={() => setSelectedOnly(value => !value)}>Selected only</button>
          </div>

          {error && <div className="manual-shipment-error" role="alert">{error}</div>}
          {loading ? <div className="manual-shipment-loading">Loading eligible orders…</div> : !visibleOrders.length ? <div className="manual-shipment-empty"><strong>No eligible orders found</strong><span>{search || selectedOnly ? 'Change the order filter.' : 'Unallocated orders due today or overdue appear here.'}</span></div> :
            <div className="manual-shipment-order-list">{visibleOrders.map(order => {
              const selected = selectedIds.includes(order.id);
              return <label className={`manual-shipment-order ${selected ? 'is-selected' : ''}`} key={order.id}>
                <input type="checkbox" checked={selected} onChange={() => toggleOrder(order.id)} />
                <span className="manual-order-check" aria-hidden="true">✓</span>
                <span className="manual-order-copy"><strong>{order.id}</strong><b>{order.customer}</b><small title={order.address}>{order.address}</small><em>{order.pincode} · {order.status} · Due {order.deliveryDate}{order.isOverdue && <i>{order.daysOverdue}d overdue</i>}</em></span>
                <span className="manual-order-load"><strong>{order.loadPoints}</strong><small>points</small><span>P {order.load.panels} · I {order.load.inverters} · B {order.load.batteries} · BOS {order.load.bos}</span>{order.locationSource === 'confirmed' && <em>Confirmed GPS</em>}</span>
              </label>;
            })}</div>}
          {data.unavailableOrders?.length > 0 && <p className="manual-shipment-unavailable">{data.unavailableOrders.length} order{data.unavailableOrders.length === 1 ? '' : 's'} hidden because product load rules could not be calculated.</p>}
        </div>

        <aside className="manual-shipment-plan-pane">
          <div className="manual-plan-section">
            <div className="manual-plan-heading"><div><span>Route plan</span><strong>{stops.length} stop{stops.length === 1 ? '' : 's'} · {finalOrderCount} order{finalOrderCount === 1 ? '' : 's'} after update</strong></div><span className={`manual-route-state ${previewing ? 'is-loading' : preview?.routeUnavailable ? 'is-fallback' : preview ? 'is-ready' : ''}`}>{previewing ? 'Updating route…' : preview?.routeUnavailable ? 'Address fallback' : preview ? 'Route updated' : 'Waiting for orders'}</span></div>
            {preview ? <div className="manual-shipment-map"><GoogleShipmentMap shipment={previewShipment} /></div> : <div className="manual-map-empty"><span>⌖</span><strong>{selectedOrders.length ? 'Updating the route automatically…' : 'Select orders to review the updated route'}</strong><small>Saved delivery pins are preferred; otherwise the complete address is geocoded.</small></div>}
            {routeError && <p className="manual-route-error">{routeError}</p>}
            {preview && !preview.routeUnavailable && <div className="manual-route-metrics"><span><b>{(Number(preview.distanceMeters || 0) / 1000).toFixed(1)} km</b> planned distance</span><span><b>{Math.max(1, Math.round(Number(preview.durationSeconds || 0) / 60))} min</b> estimated time</span></div>}
          </div>

          {!isAdding ? <div className="manual-plan-section">
            <span className="manual-field-label">Vehicle type</span>
            <div className="manual-vehicle-options">{VEHICLES.map(vehicle => <button type="button" key={vehicle.value} className={vehicleType === vehicle.value ? 'is-active' : ''} onClick={() => setVehicleType(vehicle.value)} disabled={vehicle.value === 'twoWheeler' && !data.selectedHub?.twoWheelerEnabled}><b>{vehicle.label}</b><small>{vehicle.note}</small></button>)}</div>
            {twoWheelerInvalid && <p className="manual-rule-error">Two-wheeler requires exactly one selected order containing one inverter, battery, or BOS unit and no panels.</p>}
          </div> : <div className="manual-plan-section manual-locked-vehicle"><span className="manual-field-label">Vehicle type</span><strong>{vehicleType === 'twoWheeler' ? 'Two-wheeler' : 'Four-wheeler'}</strong><small>Change the vehicle on the shipment detail page before adding orders.</small>{twoWheelerInvalid && <p className="manual-rule-error">Switch this shipment to a four-wheeler before adding another order.</p>}</div>}

          <div className="manual-plan-section manual-load-summary">
            <div><span>Planning load</span><strong className={utilization > 100 ? 'is-over' : ''}>{totals.points} / {capacity || 0} pts</strong></div>
            <div className="manual-load-track"><i style={{ width: `${Math.min(100, utilization)}%` }} /></div>
            <div className="manual-load-products"><span>P <b>{totals.panels}</b></span><span>I <b>{totals.inverters}</b></span><span>B <b>{totals.batteries}</b></span><span>BOS <b>{totals.bos}</b></span></div>
            {utilization > 100 && <p>Above the planning target. You can still {isAdding ? 'add these orders' : 'create this shipment'} after checking the actual vehicle space.</p>}
            {stopLimitExceeded && <p className="is-error">Reduce the selection to {data.selectedHub.maxStops} route stops.</p>}
            {orderLimitExceeded && <p className="is-error">Reduce the selection to {data.selectedHub.maxOrders} orders.</p>}
          </div>

          {!isAdding && <label className="manual-driver-field"><span>Driver <em>optional</em></span><select value={driverId} onChange={event => setDriverId(event.target.value)}><option value="">Assign later</option>{data.drivers.map(driver => <option key={driver.id} value={driver.id}>{driver.name || driver.email}</option>)}</select><small>Only Delivery users assigned to this hub are listed.</small></label>}
        </aside>
      </div>

      <footer className="manual-shipment-footer">
        <div><strong>{selectedOrders.length} order{selectedOrders.length === 1 ? '' : 's'} selected</strong><span>{stops.length} total drop point{stops.length === 1 ? '' : 's'} · {totals.points} total planning points</span></div>
        <button type="button" className="manual-cancel" onClick={onClose} disabled={saving}>Cancel</button>
        <button type="button" className="manual-create" onClick={handleCreate} disabled={!canCreate}>{saving ? (isAdding ? 'Adding orders…' : 'Creating shipment…') : (isAdding ? `Add ${selectedOrders.length || ''} order${selectedOrders.length === 1 ? '' : 's'}` : 'Create shipment')}</button>
      </footer>
    </section>
  </div>;
};

export default ManualShipmentModal;
